import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useMemo, useState } from "react";

import Regions from "../Regions";
import classes from "../../StateProvinceSelectionSlide.module.scss";
import { GeoTargetOption } from "../../../../../types/campaignTypes";

type SetStateAction<S> = S | ((prevState: S) => S);

type CheckBoxes = {
  CAN: Record<string, boolean>;
  USA: Record<string, boolean>;
};

type StateProvinceSelectionDasboardProps = {
  geoTargetOptions: GeoTargetOption[];
  selectedGeoTargetOptions: string[];
  setSelectedGeoTargetOptions: (selectedGeoTargetOptions: SetStateAction<string[]>) => void;
};

const transformGeoTargetOptionsToSelectOptionsState = (
  selectedGeoTargetOptions: string[],
  geoTargetOptions: GeoTargetOption[],
  country_code_3: string,
) => {
  const countryGeoTargetOptions = geoTargetOptions.filter((region) => region.key.includes(country_code_3));
  const countryGeoTargetOptionsState = countryGeoTargetOptions.reduce((accum, geoTargetOption) => {
    if (selectedGeoTargetOptions.includes(geoTargetOption.key)) {
      return {
        ...accum,
        [geoTargetOption.key]: true,
      };
    }
    return {
      ...accum,
      [geoTargetOption.key]: false,
    };
  }, {});
  return countryGeoTargetOptionsState;
};

const StateProvinceSelectionDasboard: FC<StateProvinceSelectionDasboardProps> = ({
  geoTargetOptions,
  selectedGeoTargetOptions,
  setSelectedGeoTargetOptions,
}) => {
  const [checkBoxes, setCheckBoxes] = useState<CheckBoxes>(() => {
    return {
      CAN: transformGeoTargetOptionsToSelectOptionsState(selectedGeoTargetOptions, geoTargetOptions, "CAN"),
      USA: transformGeoTargetOptionsToSelectOptionsState(selectedGeoTargetOptions, geoTargetOptions, "USA"),
    };
  });

  const usRegions = useMemo(
    () => geoTargetOptions.filter((geoTargetOption) => geoTargetOption.country_code_2 === "US"),
    [geoTargetOptions],
  );

  const canadianRegions = useMemo(
    () => geoTargetOptions.filter((geoTargetOption) => geoTargetOption.country_code_2 === "CA"),
    [geoTargetOptions],
  );

  const handleSelectAll = (event: ChangeEvent<{}>) => {
    const { name } = event.target as HTMLInputElement;

    const country_code_3 = name.includes("USA") ? "USA" : "CAN";

    const checked = checkBoxes[country_code_3][name];

    const newUsRegionsCheckBoxValues: Record<string, boolean> = {};

    for (const key of Object.keys(checkBoxes[country_code_3])) {
      if (checked) {
        newUsRegionsCheckBoxValues[key] = false;
      } else {
        newUsRegionsCheckBoxValues[key] = true;
      }
    }

    const newCheckBoxes = {
      ...checkBoxes,
      [country_code_3]: { ...newUsRegionsCheckBoxValues, [name]: !checked },
    };

    setCheckBoxes(newCheckBoxes);

    const flatCheckBoxValues = Object.values(newCheckBoxes).reduce(
      (accum, checkBoxValues) => ({
        ...accum,
        ...checkBoxValues,
      }),
      {},
    );

    const selectedOptions = Object.entries(flatCheckBoxValues)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value)
      .map(([key]) => key);

    setSelectedGeoTargetOptions(selectedOptions);
  };

  const handleChange = (event: ChangeEvent<{}>) => {
    const { name } = event.target as HTMLInputElement;

    const country_code_3 = name.includes("USA") ? "USA" : "CAN";
    const checked = checkBoxes[country_code_3][name];
    const allChecked = checkBoxes[country_code_3][`${country_code_3}_ALL`];

    const newCheckBoxes = {
      ...checkBoxes,
      [country_code_3]: {
        ...checkBoxes[country_code_3],
        [name]: !checked,
        [`${country_code_3}_ALL`]: !checked ? allChecked : false,
      },
    };

    setCheckBoxes(newCheckBoxes);

    const flatCheckBoxValues = Object.values(newCheckBoxes).reduce(
      (accum, checkBoxValues) => ({
        ...accum,
        ...checkBoxValues,
      }),
      {},
    );

    const selectedOptions = Object.entries(flatCheckBoxValues)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value)
      .map(([key]) => key);

    setSelectedGeoTargetOptions(selectedOptions);
  };

  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.regionsContainer}>
        <div className={classes.regionsColumn}>
          <div className={classes.regionsColumnHeader}>
            <Typography variant="h2" className={classes.regionsColumnHeading}>
              United States Regions
            </Typography>
          </div>
          <Regions
            regions={usRegions}
            checkBoxes={checkBoxes.USA}
            label="Select regions from the US"
            country_code_3="USA"
            handleSelectAll={handleSelectAll}
            handleChange={handleChange}
          />
        </div>

        <div className={classes.regionsColumn}>
          <div className={classes.regionsColumnHeader}>
            <Typography variant="h2" className={classes.regionsColumnHeading}>
              Canadian Regions
            </Typography>
          </div>
          <Regions
            regions={canadianRegions}
            checkBoxes={checkBoxes.CAN}
            label="Select regions from Canada"
            country_code_3="CAN"
            handleSelectAll={handleSelectAll}
            handleChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

export default StateProvinceSelectionDasboard;
